// FEAT-023: 바우처 출력 템플릿 순수 헬퍼
// 스펙 SPEC-RG-05 — 비율(%) 기반 QR 박스 좌표 관리
//
// 설계 원칙:
// - 위치 좌표는 이미지 기준 비율(%)로 저장 (해상도/스케일 무관)
// - Phase 1은 정사각 (widthPct === heightPct)
// - clamp 규칙: 0 <= x + width <= 100, 0 <= y + height <= 100

// Phase 1 기본 QR 박스 — 하단 중앙 25% 정사각
export const DEFAULT_QR_BOX = Object.freeze({
    xPct: 37.5,
    yPct: 70,
    widthPct: 25,
    heightPct: 25,
});

// 라이선스 type(Initial/Renewal) 라벨 박스 — QR 바로 아래, 좁은 너비 default.
// widthPct=15 (xPct=42.5 중앙). fontSize 가 width 제약으로 자동 작게 계산되어
// 박스 안에 자연스러운 비율로 fit.
export const DEFAULT_TYPE_BOX = Object.freeze({
    xPct: 42.5,
    yPct: 96.75,
    widthPct: 15,
    heightPct: 2.25,
});

// BUG-097: QR 박스 기준으로 시리얼 영역을 파생 계산.
// QR SVG 는 1:1 정사각으로 렌더되므로 render box 의 short-side 가 실제 이미지 크기.
// serial 너비/X 를 QR 실제 이미지 크기에 맞추어 텍스트가 QR 바로 아래 정렬되도록 함.
// qrBox.heightPct < SERIAL_HEIGHT_PCT 인 edge case 는 QR height 를 최소 50% 로 clamp.
export const SERIAL_HEIGHT_PCT = 2.5;

export const deriveSerialLayout = (qrBox, { pageWidthMm, pageHeightMm }) => {
    const qrRenderHeightPct = Math.max(
        qrBox.heightPct - SERIAL_HEIGHT_PCT,
        qrBox.heightPct * 0.5,
    );
    const physWMm = (qrBox.widthPct / 100) * pageWidthMm;
    const physHMm = (qrRenderHeightPct / 100) * pageHeightMm;
    const actualSizeMm = Math.min(physWMm, physHMm);
    const actualWidthPct = (actualSizeMm / pageWidthMm) * 100;
    const actualXPct = qrBox.xPct + (qrBox.widthPct - actualWidthPct) / 2;
    return {
        qrRenderBox: { ...qrBox, heightPct: qrRenderHeightPct },
        serialBox: {
            xPct: actualXPct,
            yPct: qrBox.yPct + qrRenderHeightPct,
            widthPct: actualWidthPct,
            heightPct: SERIAL_HEIGHT_PCT,
        },
    };
};

const MIN_SIZE_PCT = 1;
const MAX_PCT = 100;

const clampNumber = (value, min, max) => {
    if (!Number.isFinite(value)) return min;
    return Math.max(min, Math.min(max, value));
};

// qrBox 경계 clamp — 0 미만/100 초과 좌표 보정, 컨테이너 밖으로 넘어가지 않도록 x/y 축소
export const clampQrBox = (box) => {
    if (!box || typeof box !== 'object') return { ...DEFAULT_QR_BOX };
    const widthPct = clampNumber(box.widthPct, MIN_SIZE_PCT, MAX_PCT);
    const heightPct = clampNumber(box.heightPct, MIN_SIZE_PCT, MAX_PCT);
    let xPct = clampNumber(box.xPct, 0, MAX_PCT);
    let yPct = clampNumber(box.yPct, 0, MAX_PCT);
    if (xPct + widthPct > MAX_PCT) xPct = Math.max(0, MAX_PCT - widthPct);
    if (yPct + heightPct > MAX_PCT) yPct = Math.max(0, MAX_PCT - heightPct);
    return { xPct, yPct, widthPct, heightPct };
};

// typeBox 도 동일 clamp 정책 (정사각 강제 X — 가로 길이 자유, 일반적 텍스트 박스).
export const clampTypeBox = (box) => {
    if (!box || typeof box !== 'object') return { ...DEFAULT_TYPE_BOX };
    const widthPct = clampNumber(box.widthPct, MIN_SIZE_PCT, MAX_PCT);
    const heightPct = clampNumber(box.heightPct, MIN_SIZE_PCT, MAX_PCT);
    let xPct = clampNumber(box.xPct, 0, MAX_PCT);
    let yPct = clampNumber(box.yPct, 0, MAX_PCT);
    if (xPct + widthPct > MAX_PCT) xPct = Math.max(0, MAX_PCT - widthPct);
    if (yPct + heightPct > MAX_PCT) yPct = Math.max(0, MAX_PCT - heightPct);
    return { xPct, yPct, widthPct, heightPct };
};

// 저장된 qrBox가 유효한지 검증 (Save dispatch 전 가드, 서버 응답 validation 동시 사용)
export const isValidQrBox = (box) => {
    if (!box || typeof box !== 'object') return false;
    const keys = ['xPct', 'yPct', 'widthPct', 'heightPct'];
    for (const k of keys) {
        const v = box[k];
        if (typeof v !== 'number' || !Number.isFinite(v)) return false;
        if (v < 0 || v > MAX_PCT) return false;
    }
    if (box.widthPct < MIN_SIZE_PCT) return false;
    if (box.heightPct < MIN_SIZE_PCT) return false;
    // 합산 범위 — x+width / y+height 가 이미지 컨테이너 밖으로 넘어가면 안 됨
    if (box.xPct + box.widthPct > MAX_PCT) return false;
    if (box.yPct + box.heightPct > MAX_PCT) return false;
    return true;
};

/**
 * react-rnd 의 onDragStop / onResizeStop 에서 반환하는 px 좌표를 비율(%)로 환산.
 *
 * 컨테이너 미측정(ResizeObserver 이전) 상태에서 호출되면 width/height 가 0 이므로
 * 분모 0 을 피하기 위해 `{xPct:0, yPct:0, widthPct:0, heightPct:0}` sentinel 을 반환한다.
 * widthPct=0 / heightPct=0 은 `isValidQrBox` MIN_SIZE_PCT(1) 미만으로 거절되어 저장이
 * 차단되므로 호출부는 반드시 `containerSize.width > 0` 를 보장한 뒤 호출해야 한다.
 * (QrPositionOverlay 는 `containerSize.width > 0` 가드로 Rnd 자체가 렌더되지 않는다.)
 */
export const toPctFromPx = (pxBox, containerSize) => {
    const w = containerSize?.width || 0;
    const h = containerSize?.height || 0;
    if (w <= 0 || h <= 0) {
        return { xPct: 0, yPct: 0, widthPct: 0, heightPct: 0 };
    }
    return {
        xPct: (pxBox.x / w) * 100,
        yPct: (pxBox.y / h) * 100,
        widthPct: (pxBox.width / w) * 100,
        heightPct: (pxBox.height / h) * 100,
    };
};

// 비율을 절대 위치 CSS 로 변환 (VoucherPrintArea / QrPositionOverlay 공용)
export const pctToCss = (box) => ({
    position: 'absolute',
    left: `${box.xPct}%`,
    top: `${box.yPct}%`,
    width: `${box.widthPct}%`,
    height: `${box.heightPct}%`,
});

// IMP-107: VoucherTemplateEditor 에서 이전 — 배경 이미지 박스 / 페이지 마진 기본값 + clamp.

// 배경 이미지 박스 기본값 — 캔버스 전체.
export const DEFAULT_BG_BOX = Object.freeze({ xPct: 0, yPct: 0, widthPct: 100, heightPct: 100 });

// 페이지 마진 기본값(%) — 좌/우/상/하 각각. Fit 시 콘텐츠 영역 계산에 사용.
export const DEFAULT_MARGINS = Object.freeze({ top: 5, right: 5, bottom: 5, left: 5 });

// bbox 0~100 범위 clamp + width/height 1 이상 보장.
export const clampBgBox = (b) => {
    if (!b || typeof b !== 'object') return { xPct: 0, yPct: 0, widthPct: 100, heightPct: 100 };
    const x = Math.max(0, Math.min(100, Number(b.xPct) || 0));
    const y = Math.max(0, Math.min(100, Number(b.yPct) || 0));
    const w = Math.max(1, Math.min(100 - x, Number(b.widthPct) || 100));
    const h = Math.max(1, Math.min(100 - y, Number(b.heightPct) || 100));
    return { xPct: x, yPct: y, widthPct: w, heightPct: h };
};

// 마진 0~45 범위 clamp (좌+우, 상+하 합 90 이하 보장 — 콘텐츠 영역 최소 10% 확보)
export const clampMargins = (m) => {
    if (!m || typeof m !== 'object') return { top: 5, right: 5, bottom: 5, left: 5 };
    const cap = (v) => Math.max(0, Math.min(45, Number(v) || 0));
    let top = cap(m.top);
    let bottom = cap(m.bottom);
    let left = cap(m.left);
    let right = cap(m.right);
    if (top + bottom > 90) { const k = 90 / (top + bottom); top *= k; bottom *= k; }
    if (left + right > 90) { const k = 90 / (left + right); left *= k; right *= k; }
    return { top, right, bottom, left };
};

// companyId 경로 탈출 방어 — S3 key prefix 삽입 전 whitelist 패턴 검증.
// 현재 포맷은 RAY+숫자지만 JWT claim 기반 임의 문자열 허용 시 `../` 등이 key 에 흘러들 수 있다.
const COMPANY_ID_PATTERN = /^[A-Za-z0-9_-]+$/;
export const isSafeCompanyId = (v) => typeof v === 'string' && COMPANY_ID_PATTERN.test(v);
